import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { ClientEvent, SyncState } from '../constants/matrixEvents';
import matrixService from '../services/matrixService';
import networkService from '../services/networkService';
import logger from '../utils/logger';

const HIDE_DELAY_MS = 5000;

/**
 * Encapsulates network-status state and the Matrix sync-retry action.
 *
 * Keeps service imports out of presentational components by exposing:
 *  - `status`               – 'hidden' | 'offline' | 'online'
 *  - `isReconnectPending`   – true while a retrySyncImmediately call is in-flight
 *  - `hasClient`            – true when a Matrix client is currently available
 *  - `retrySyncImmediately` – triggers an immediate Matrix sync retry
 */
const useNetworkStatus = () => {
  const getInitialStatus = () => {
    const browserOnline = networkService.getIsOnline();
    if (!browserOnline) return 'offline';

    const client = matrixService.getClient?.();
    if (!client || typeof client.getSyncState !== 'function') return 'hidden';

    const syncState = client.getSyncState();
    const isPreparedState = (s) => (
      s === (SyncState && SyncState.Prepared) ||
      s === (SyncState && SyncState.Syncing) ||
      s === 'PREPARED' ||
      s === 'SYNCING'
    );

    return isPreparedState(syncState) ? 'hidden' : 'offline';
  };

  const [status, setStatus] = useState(getInitialStatus);
  const [isReconnectPending, setIsReconnectPending] = useState(false);
  // Bumped on session invalidation so `hasClient` re-evaluates after logout.
  const [, bumpRender] = useReducer((n) => n + 1, 0);
  const hideTimerRef = useRef(null);
  const hasShownOfflineRef = useRef(!networkService.getIsOnline());

  // Read on every render so login/logout transitions are reflected without
  // depending on a network-event arrival.
  const hasClient = Boolean(matrixService.getClient?.());

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const showOffline = useCallback(() => {
    clearHideTimer();
    hasShownOfflineRef.current = true;
    setIsReconnectPending(false);
    setStatus('offline');
  }, [clearHideTimer]);

  const showOnlineConfirmation = useCallback(() => {
    if (!hasShownOfflineRef.current) return;

    clearHideTimer();
    setIsReconnectPending(false);
    setStatus('online');
    hideTimerRef.current = setTimeout(() => {
      setStatus('hidden');
      hideTimerRef.current = null;
      hasShownOfflineRef.current = false;
    }, HIDE_DELAY_MS);
  }, [clearHideTimer]);

  useEffect(() => {
    const evaluateOnline = ({ isOnline }) => {
      if (!isOnline) {
        showOffline();
        return;
      }

      // Browser believes we're online — consult Matrix sync if client exists
      const client = matrixService.getClient?.();
      if (!client || typeof client.getSyncState !== 'function') {
        showOnlineConfirmation();
        return;
      }

      const state = client.getSyncState();
      const isPreparedState = (s) => (
        s === (SyncState && SyncState.Prepared) ||
        s === (SyncState && SyncState.Syncing) ||
        s === 'PREPARED' ||
        s === 'SYNCING'
      );

      if (isPreparedState(state)) {
        showOnlineConfirmation();
      } else {
        // Matrix sync not yet prepared — treat as offline until sync indicates otherwise
        showOffline();
      }
    };

    const unsubscribe = networkService.subscribe(evaluateOnline);

    return () => {
      clearHideTimer();
      unsubscribe();
    };
  }, [clearHideTimer, showOffline, showOnlineConfirmation]);

  // Listen to Matrix client sync transitions to update network status when
  // the browser reports online but the client experiences sync errors.
  useEffect(() => {
    let clientForSyncListener = null;
    let syncListener = null;
    let retryTimer = null;

    const attach = () => {
      const client = matrixService.getClient?.();
      if (!client || typeof client.on !== 'function') return false;

      syncListener = (state) => {
        const isPreparedState = (s) => (
          s === (SyncState && SyncState.Prepared) ||
          s === (SyncState && SyncState.Syncing) ||
          s === 'PREPARED' ||
          s === 'SYNCING'
        );

        if (isPreparedState(state)) {
          // Only show online if the browser also reports online
          if (networkService.getIsOnline()) showOnlineConfirmation();
        } else if (state === (SyncState && SyncState.Error) || state === 'ERROR') {
          // STOPPED is deliberately excluded: it signals an intentional
          // client.stopClient() (e.g. logout), not a network failure. Showing
          // "Connection lost" during logout teardown is misleading — the user
          // is leaving on purpose.
          showOffline();
        }
      };

      clientForSyncListener = client;
      client.on(ClientEvent.Sync, syncListener);

      if (typeof client.getSyncState === 'function') {
        syncListener(client.getSyncState());
      }

      return true;
    };

    if (!attach()) {
      retryTimer = setInterval(() => {
        if (attach()) {
          clearInterval(retryTimer);
          retryTimer = null;
        }
      }, 500);
    }

    return () => {
      if (retryTimer) clearInterval(retryTimer);
      if (clientForSyncListener && syncListener && clientForSyncListener.off) {
        clientForSyncListener.off(ClientEvent.Sync, syncListener);
      }
    };
  }, [showOffline, showOnlineConfirmation]);

  useEffect(() => {
    const handleSessionInvalidated = () => {
      // The session is gone — there is nothing to "reconnect" to. Clear any
      // offline banner the sync-teardown may have triggered, so the user
      // does not see a misleading "Connection lost" flash on the way to /login.
      clearHideTimer();
      hasShownOfflineRef.current = false;
      setIsReconnectPending(false);
      setStatus('hidden');
      bumpRender();
    };
    window.addEventListener('matrix:session-invalidated', handleSessionInvalidated);
    return () => {
      window.removeEventListener('matrix:session-invalidated', handleSessionInvalidated);
    };
  }, [clearHideTimer]);

  const retrySyncImmediately = useCallback(async () => {
    if (isReconnectPending) return;
    if (!matrixService.getClient?.()) return;

    setIsReconnectPending(true);

    try {
      await matrixService.retrySyncImmediately();
    } catch (error) {
      logger.warn('useNetworkStatus: reconnect attempt failed', {
        errorMessage: error?.message,
        errorName: error?.name,
      });
    } finally {
      setIsReconnectPending(false);
    }
  }, [isReconnectPending]);

  return { status, isReconnectPending, hasClient, retrySyncImmediately };
};

export default useNetworkStatus;
